import { Progress, Space, Tag, Typography, theme } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { countCompleteFields, getTrackableFieldKeys } from "./GameFieldItems";
import { getMissingFieldLabel, scrollToField } from "../utils/fieldLabels";

interface CompletionProgressBarProps {
  missingFields: string[];
  isSeafreight: boolean;
  isFcl?: boolean;
  productCount: number;
}

export const CompletionProgressBar = ({
  missingFields,
  isSeafreight,
  isFcl = false,
  productCount,
}: CompletionProgressBarProps) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const trackableKeys = getTrackableFieldKeys(isSeafreight, productCount, isFcl);
  const completeCount = countCompleteFields(missingFields, trackableKeys);
  const total = trackableKeys.length;
  const percent = total > 0 ? Math.round((completeCount / total) * 100) : 0;
  const allDone = missingFields.length === 0 && total > 0;

  return (
    <div
      style={{
        marginBottom: 24,
        padding: "16px 20px",
        borderRadius: 12,
        background: allDone ? token.colorSuccessBg : token.colorFillAlter,
        border: `1px solid ${allDone ? token.colorSuccessBorder : token.colorBorderSecondary}`,
      }}
    >
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Space align="center" style={{ width: "100%", justifyContent: "space-between" }}>
          <Typography.Text strong>
            {allDone
              ? t("bookingWizardV2.details.allComplete")
              : t("bookingWizardV2.details.progressLabel", {
                  percent,
                  remaining: missingFields.length,
                })}
          </Typography.Text>
          {allDone && (
            <CheckCircleOutlined style={{ color: token.colorSuccess, fontSize: 20 }} />
          )}
        </Space>

        <Progress
          percent={percent}
          showInfo={false}
          strokeColor={allDone ? token.colorSuccess : token.colorPrimary}
          size="small"
        />

        {missingFields.length > 0 && (
          <Space size={[8, 8]} wrap>
            {missingFields.map((fieldKey) => (
              <Tag
                key={fieldKey}
                color="warning"
                style={{ margin: 0, cursor: "pointer" }}
                onClick={() => scrollToField(fieldKey)}
              >
                {getMissingFieldLabel(fieldKey, t)}
              </Tag>
            ))}
          </Space>
        )}
      </Space>
    </div>
  );
};
